import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBookmark, FiExternalLink, FiX, FiStar } = FiIcons;

export default function QuickAccessDock({
  pinnedBookmarks = [],
  onOpenBookmark,
  onUnpin,
  accentColor = '#c2ffe0',
  themeColors: tc = {},
  isDark: isDarkProp,
}) {
  if (pinnedBookmarks.length === 0) {
    return null;
  }

  const isDark = isDarkProp !== undefined
    ? Boolean(isDarkProp)
    : !tc?.card?.toLowerCase().includes('ffffff') && !tc?.card?.startsWith('rgba(255');

  return (
    <div className="w-full max-w-5xl mx-auto mb-6 px-4">
      <div className={`flex items-center gap-2 mb-2 text-xs font-semibold uppercase tracking-wider ${
        isDark ? 'text-[#777]' : 'text-slate-500'
      }`}>
        <SafeIcon icon={FiStar} className="w-3.5 h-3.5 text-amber-400" />
        <span>Quick Access Dock</span>
        <span className={`text-[10px] font-normal lowercase ${isDark ? 'text-[#555]' : 'text-slate-400'}`}>
          ({pinnedBookmarks.length} pinned)
        </span>
      </div>

      <div
        className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none no-scrollbar"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        data-no-scrollbar="true"
      >
        {pinnedBookmarks.map((bm) => (
          <motion.div
            key={bm.id}
            whileHover={{ y: -2, scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`group relative flex items-center gap-2.5 px-3 py-2 rounded-xl border shadow-sm hover:shadow-md transition-all cursor-pointer shrink-0 ${
              isDark
                ? 'bg-[#141416]/90 hover:bg-[#1a1a1f] border-[#24242a] hover:border-[#383842]'
                : 'bg-white/90 hover:bg-white border-slate-200 hover:border-slate-300'
            }`}
            onClick={() => onOpenBookmark(bm.url)}
          >
            {/* Favicon */}
            <div className={`w-5 h-5 rounded flex items-center justify-center shrink-0 overflow-hidden ${
              isDark ? 'bg-[#222]' : 'bg-slate-100'
            }`}>
              {bm.logoUrl ? (
                <img
                  src={bm.logoUrl}
                  alt=""
                  className="w-4 h-4 object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <SafeIcon icon={FiBookmark} className={`w-3.5 h-3.5 ${isDark ? 'text-[#aaa]' : 'text-slate-400'}`} />
              )}
            </div>

            {/* Title */}
            <span className={`text-xs font-medium max-w-[120px] truncate ${
              isDark ? 'text-white' : 'text-slate-800'
            }`}>
              {bm.title}
            </span>

            {/* External link hint */}
            <SafeIcon icon={FiExternalLink} className={`w-3 h-3 transition-colors ${
              isDark ? 'text-[#555] group-hover:text-[#aaa]' : 'text-slate-400 group-hover:text-slate-700'
            }`} />

            {/* Unpin button on hover */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onUnpin(bm.id);
              }}
              title="Unpin from dock"
              className={`opacity-0 group-hover:opacity-100 p-0.5 rounded hover:text-red-400 hover:bg-red-500/10 transition-all ml-1 cursor-pointer ${
                isDark ? 'text-[#666]' : 'text-slate-400'
              }`}
            >
              <SafeIcon icon={FiX} className="w-3 h-3" />
            </button>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
